/*!
 * vue-router-permissions v0.10.14 
 * (c) 2020 Alex Tkachenko
 * Released under the MIT License.
 */
import { PermissionsAccessState, PermissionsAvailable } from '@aspectus/vue-permissions';
import { withDefaultProps } from '@aspectus/vue-utils';

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    if (enumerableOnly) symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    });
    keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread2(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};

    if (i % 2) {
      ownKeys(Object(source), true).forEach(function (key) {
        _defineProperty(target, key, source[key]);
      });
    } else if (Object.getOwnPropertyDescriptors) {
      Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
      ownKeys(Object(source)).forEach(function (key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
      });
    }
  }

  return target;
}

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _nonIterableRest();
}

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

function _iterableToArrayLimit(arr, i) {
  if (!(Symbol.iterator in Object(arr) || Object.prototype.toString.call(arr) === "[object Arguments]")) {
    return;
  }

  var _arr = [];
  var _n = true;
  var _d = false;
  var _e = undefined;

  try {
    for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance");
}

/* eslint-disable prefer-rest-params */
function collectRoutePermissionCheckers(route) {
  return route.matched.reduce(function (acc, r) {
    var checker = r.meta.accessConfig && r.meta.accessConfig.checker;

    if (checker) {
      acc.push([checker, r]);
    }

    return acc;
  }, []);
}
function routeProhibitionResolver(route) {
  var _route$meta$accessCon = route.meta.accessConfig,
      resolver = _route$meta$accessCon.resolver,
      next = _route$meta$accessCon.next,
      _route$meta$accessCon2 = _route$meta$accessCon.redirect,
      redirect = _route$meta$accessCon2 === void 0 ? false : _route$meta$accessCon2;

  if (resolver) {
    return resolver(route) || false;
  }

  return next || redirect || false;
}
function onHasRouteAccess(r, vm) {
  var parameters = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  var checkers = collectRoutePermissionCheckers(r);

  if (!(checkers && checkers.length)) {
    return Promise.resolve(true);
  }

  var executors = checkers.map(function (_ref) {
    var _ref2 = _slicedToArray(_ref, 2),
        checker = _ref2[0],
        route = _ref2[1];

    return checker.onHasAccess(vm, _objectSpread2({
      route: route
    }, parameters)).catch(function (e) {
      console.warn(e);
      throw route;
    }).then(function () {
      return true;
    });
  });
  return Promise.all(executors);
}
function resolveRouteNext(r, vm) {
  var parameters = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  return onHasRouteAccess(r, vm, parameters).catch(routeProhibitionResolver);
}
var accessGuard = function accessGuard() {
  var parameters = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return function (to, from, next) {
    return resolveRouteNext(to, null, parameters).then(function (result) {
      return next(result);
    });
  };
};
var accessConfig = function accessConfig(checker) {
  var redirect = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
  var options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  return _objectSpread2({
    checker: checker,
    redirect: redirect
  }, options);
};
function accessMeta() {
  return {
    accessConfig: accessConfig.apply(this, arguments)
  };
}

var RouteAccessState = {
  name: 'route-access-state',
  props: {
    to: {}
  },
  computed: {
    permissions: function permissions() {
      return collectRoutePermissionCheckers(this.$router.matcher.match(this.to)).map(function (x) {
        return x[0];
      });
    }
  },
  render: function render(h) {
    var _this = this;

    return h(PermissionsAccessState, {
      props: {
        permissions: this.permissions
      },
      scopedSlots: {
        default: function _default(props) {
          return _this.$scopedSlots.default(props);
        }
      }
    });
  }
};

/* eslint-disable prefer-object-spread */
var RouteAvailable = Object.assign(withDefaultProps({
  checker: function checker() {
    return RouteAccessState;
  }
}, PermissionsAvailable), {
  name: 'route-available'
});

function install(Vue) {
  var _ref = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
      _ref$stateName = _ref.stateName,
      stateName = _ref$stateName === void 0 ? RouteAccessState.name : _ref$stateName,
      _ref$availableName = _ref.availableName,
      availableName = _ref$availableName === void 0 ? RouteAvailable.name : _ref$availableName;

  Vue.component(stateName, RouteAccessState);
  Vue.component(availableName, RouteAvailable);
}

var main = {
  install: install
};

export default main;
export { RouteAccessState, RouteAvailable, accessConfig, accessGuard, accessMeta, collectRoutePermissionCheckers, onHasRouteAccess, resolveRouteNext, routeProhibitionResolver };
